import { useEffect, useMemo, useState } from "react"
import { Card, CardContent } from "./ui/card"
import { HeartPulse, Activity, Clock } from "lucide-react"
import { DataService } from "../services/data.service"
import { BloodPressureReading } from "../models/bloodPressure.model"
import { DateTime } from "luxon"
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts"
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "./ui/chart"

export interface BloodPressureReportProps {
    deploymentId: number
    timezone: string
    // Bumped by the page's 15-min silent refresh to re-pull readings; the
    // deploymentId alone is stable, so without this the panel never refreshes
    // on the page cadence. Data isn't cleared on refetch, so no flicker.
    refreshKey?: number
}

// How many days of history to request. Default 30; the toggle lets a caregiver
// widen/narrow the window.
type Range = 7 | 30 | 90

// Fallback window used when the selected range comes back empty. A monitor that
// stopped being used months ago would otherwise hide the whole card (and with it
// the range toggle), so there'd be no way to reach the readings that do exist.
// ~10 years; the server applies no cap and BP rows are sparse (tens, not thousands).
const ALL_HISTORY_DAYS = 3650

const chartConfig: ChartConfig = {
    systolic: { label: "Systolic", color: "#dc2626" },
    diastolic: { label: "Diastolic", color: "#2563eb" },
    pulse: { label: "Pulse", color: "#16a34a" },
}

function rangeButtonCls(active: boolean) {
    return active
        ? 'px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg'
        : 'px-4 py-2 text-sm font-medium bg-gray-200 rounded-lg hover:bg-gray-300'
}

export function BloodPressureReport(props: BloodPressureReportProps) {
    const [readings, setReadings] = useState<BloodPressureReading[] | null>(null)
    const [days, setDays] = useState<Range>(30)
    // True when the selected range was empty and we widened to full history —
    // drives the note that tells the caregiver what they're actually looking at.
    const [fellBack, setFellBack] = useState(false)
    // BM67-style monitors store readings per user slot (0, 4, …). Default to
    // slot 0; the selector below lets a caregiver switch when more than one
    // person shares the monitor.
    const [selectedUser, setSelectedUser] = useState<number>(0)

    useEffect(() => {
        let cancelled = false
        // Don't clear existing data on a range switch — keeps the panel from
        // flickering out and back in (and from collapsing the layout) while the
        // new window loads.
        const load = async () => {
            let rows = await DataService.getBloodPressure(props.deploymentId, days)
            let widened = false
            if (rows.length === 0) {
                rows = await DataService.getBloodPressure(props.deploymentId, ALL_HISTORY_DAYS)
                widened = rows.length > 0
            }
            if (cancelled) return
            setReadings(rows)
            setFellBack(widened)
        }
        load().catch(() => { if (!cancelled) { setReadings([]); setFellBack(false) } })
        return () => { cancelled = true }
    }, [props.deploymentId, days, props.refreshKey])

    // User slots that actually have readings in this window (null → slot 0),
    // sorted, so the selector only offers people the monitor really recorded.
    const availableUsers = useMemo(
        () => Array.from(new Set((readings || []).map(r => r.user_id ?? 0))).sort((a, b) => a - b),
        [readings]
    )

    // Keep the selection valid: default is slot 0, but if slot 0 has no readings
    // fall back to the lowest slot that does, so the graph isn't empty on load.
    useEffect(() => {
        if (availableUsers.length && !availableUsers.includes(selectedUser)) {
            setSelectedUser(availableUsers[0])
        }
    }, [availableUsers, selectedUser])

    // Readings for the selected user only — the graph must not mix two people's
    // blood pressure into one series.
    const userReadings = useMemo(
        () => (readings || []).filter(r => (r.user_id ?? 0) === selectedUser),
        [readings, selectedUser]
    )

    // Chronological (ascending) series for the chart, with a short per-point
    // label. Readings come back newest-first from the server.
    const chartData = useMemo(() => {
        return [...userReadings]
            .filter(r => r.measurement_time)
            .sort((a, b) => (a.measurement_time! < b.measurement_time! ? -1 : 1))
            .map(r => {
                const dt = DateTime.fromISO(r.measurement_time!).setZone(props.timezone)
                return {
                    label: dt.toFormat("MMM d"),
                    // Full date + time (with seconds) for the hover tooltip; the
                    // axis keeps the short "MMM d" label to stay readable.
                    fullTime: dt.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS),
                    systolic: r.systolic,
                    diastolic: r.diastolic,
                    pulse: r.pulse_rate,
                }
            })
    }, [userReadings, props.timezone])

    // Most recent reading for the selected user. Don't trust server array order —
    // pick the max measurement_time so a corrupt-clock reading can't masquerade as
    // "latest". Missing measurement_time sorts as oldest.
    const latest = useMemo(() => {
        if (!userReadings.length) return null
        return userReadings.reduce((best, r) =>
            (r.measurement_time ?? "") > (best.measurement_time ?? "") ? r : best
        )
    }, [userReadings])

    // Only show the panel when blood pressure was actually read for this
    // account — nothing renders while loading or when there's no data.
    if (!readings || readings.length === 0) return null

    return (
        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="blood_pressure_report">
            <div className="flex justify-between items-center border-b pb-2">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <HeartPulse className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} /> Blood Pressure
                </h3>
                {availableUsers.length > 1 && (
                    <select
                        className="px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-800 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        value={selectedUser}
                        onChange={e => setSelectedUser(Number(e.target.value))}
                    >
                        {availableUsers.map(u => (
                            <option key={u} value={u}>User {u}</option>
                        ))}
                    </select>
                )}
            </div>

            <div className="flex justify-center gap-2">
                <button className={rangeButtonCls(days === 7)} onClick={() => setDays(7)}>7d</button>
                <button className={rangeButtonCls(days === 30)} onClick={() => setDays(30)}>30d</button>
                <button className={rangeButtonCls(days === 90)} onClick={() => setDays(90)}>90d</button>
            </div>

            {fellBack && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    No readings in the last {days} days — showing the full history instead.
                </p>
            )}

            {latest && (
                <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                        <div className="flex justify-between items-center">
                            <p className="text-sm font-medium text-gray-800">{latest.systolic} / {latest.diastolic} mmHg</p>
                            <HeartPulse className="text-gray-800" />
                        </div>
                        <span className="text-xs text-gray-500">Latest Blood Pressure</span>
                    </div>
                    <div className="flex flex-col p-2 bg-gray-100 rounded-lg">
                        <div className="flex justify-between items-center">
                            <p className="text-sm font-medium text-gray-800">{latest.pulse_rate != null ? `${latest.pulse_rate} bpm` : '—'}</p>
                            <Activity className="text-gray-800" />
                        </div>
                        <span className="text-xs text-gray-500">Pulse</span>
                    </div>
                </div>
            )}

            {latest?.measurement_time && (
                <div className="flex items-center bg-gray-100 p-2 rounded-lg space-x-3">
                    <div className="bg-gray-100 p-2 rounded-lg">
                        <Clock className="text-gray-800" />
                    </div>
                    <div className="flex flex-col">
                        <p className="text-sm font-medium text-gray-800">
                            {DateTime.fromISO(latest.measurement_time).setZone(props.timezone).toLocaleString(DateTime.DATETIME_MED)}
                        </p>
                        {/* Relative age matters now that the panel will happily show a
                            months-old reading rather than hide itself. */}
                        <span className="text-xs text-gray-500">
                            Last measured · {DateTime.fromISO(latest.measurement_time).setZone(props.timezone).toRelative()}
                        </span>
                    </div>
                </div>
            )}

            <CardContent className="p-0">
                <ChartContainer config={chartConfig}>
                    <LineChart accessibilityLayer data={chartData} margin={{ top: 0, left: 0, right: 0, bottom: 0 }}>
                        <CartesianGrid vertical={false} />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} angle={-60} />
                        <YAxis tickLine={false} axisLine={false} tickMargin={10} domain={['dataMin - 10', 'dataMax + 10']} />
                        <ChartTooltip
                            cursor={false}
                            content={
                                <ChartTooltipContent
                                    indicator="line"
                                    labelFormatter={(_label, payload) =>
                                        (payload && (payload[0] as any)?.payload?.fullTime) || ''
                                    }
                                />
                            }
                        />
                        <Line dataKey="systolic" type="monotone" stroke="#dc2626" strokeWidth={2} dot={{ r: 2 }} connectNulls={true} isAnimationActive={false} />
                        <Line dataKey="diastolic" type="monotone" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} connectNulls={true} isAnimationActive={false} />
                        <Line dataKey="pulse" type="monotone" stroke="#16a34a" strokeWidth={2} dot={{ r: 2 }} connectNulls={true} isAnimationActive={false} />
                    </LineChart>
                </ChartContainer>
            </CardContent>
        </Card>
    )
}
